<template>
<div data-component="FilterDetailsLocation.vue" class="flex flex-col-reverse lg:flex-row" :class="{'inactive-panel': !filter.isActive}">
	<div class="basis-1/2">
		<div
			v-for="(group, key) in filter.locations"
			:key="key"
			class="-mb-px border-neutral-100 border-y"
		>
			<!-- Nagłówek grupy -->
			<div
				class="flex items-center h-12 px-3"
				@click="group.expanded = !group.expanded"
			>
				<div
					class="mr-2 cursor-pointer input-control"
					@click.stop="toggleLocation(String(key))"
				>
					<div
						class="is-checkbox"
						:class="{ 'is-checked': group.selected }"
					/>
				</div>
				<div>
					<div class="text-base" v-html="group.name"/>
					<div v-if="group.selected" class="text-xs text-neutral-700">
						<template v-if="group.selected !== Object.keys(group.items).length">
							{{ `${group.selected} z ${Object.keys(group.items).length} ${String(key) === 'locationForeign' ? 'krajów' : 'powiatów'}` }}
						</template>
						<template v-else>
							Wszystkie {{ String(key) === 'locationForeign' ? 'kraje' : 'powiaty' }}
						</template>
					</div>
				</div>
				<BaseIcon
					name="chevron-right"
					size="0.75"
					class="ml-auto text-black"
					:class="group.expanded ? '-rotate-90' : 'rotate-90'"
				/>
			</div>

			<!-- Elementy grupy -->
			<div
				v-if="group.expanded"
				class="flex flex-wrap items-center px-3 pt-3 border-t border-black/10"
			>
				<template v-for="(item, itemKey, i) in group.items">
					<div
						v-if="i === 0 && String(key) === 'locationForeign'"
						:key="`${itemKey}ue`"
						class="px-2 py-1 mb-3 mr-1 text-xs border rounded-full border-black/10"
						:class="{ 'is-selected | text-info !border-info': isEUSelected }"
						@click.stop="toggleEU"
						v-html="'UE'"
					/>
					<div
						v-if="item.name !== 'Polska'"
						:key="itemKey"
						class="px-2 py-1 mb-3 mr-1 text-xs border rounded-full border-black/10"
						:class="{ 'is-selected | text-info !border-info': item.selected }"
						@click.stop="toggleLocation(String(itemKey), false)"
						v-html="item.name"
					/>
				</template>
			</div>
		</div>
	</div>

	<!-- Mapa -->
	<div
		class="sticky lg:static top-16 lg:top-24 lg:px-3 w-full basis-1/2 aspect-[2/1] lg:aspect-square overflow-hidden"
		:class="{ 'inactive-panel': !filter.isActive }"
	>
		<div class="sticky top-0 rounded bg-neutral-100 aspect-square">
			<div
				ref="GoogleMapElement"
				class="absolute inset-0 flex items-center justify-center"
			/>
			<DummyElement @mounted="onMapVisible"/>
		</div>
	</div>
</div>
</template>

<script lang="ts">
import { mapState, mapWritableState } from 'pinia';
import { useServicesStore } from '../../../../stores/services';
import DummyElement from '../../../../globals/components/global/DummyElement.vue';
import Filter from '../../../../types/interfaces/services/Filter';
import { Services } from '../../../../types/interfaces/services/Services';

/**
 * Komponent wyświetlający ekran edycji lokalizacji filtra
 */
export default {
	name: 'FilterDetailsLocation',
	components: {
		DummyElement,
	},
	props: {
		/**
		 * Główny obiekt usług
		 */
		servicesObject: {
			type: Object as () => Services,
			required: true,
		},

		/**
		 * Aktualnie wybrany filtr
		 */
		filter: {
			type: Object as () => Filter,
			required: true,
		},
	},
	emits: ['save', 'recalculate-potential-matches'],
	data() {
		return {
			/**
			 * Ustawienia mapy z lokalizacjami
			 */
			map: {
				/** Google Maps API */
				google: undefined as any,
				/** Style dla wybranych rejonów do kliknięcia */
				styles: {
					default: {
						strokeWeight: 2,
						strokeColor: '#ff7800',
						fillColor: '#ff7800',
					},
					false: {
						fillOpacity: 0.0,
						strokeOpacity: 0.15,
						strokeColor: '#808080',
					},
					true: {
						fillOpacity: 0.25,
						strokeOpacity: 1.0,
						strokeColor: '#ff7800',
					},
					blocked: {
						fillOpacity: 0.25,
						strokeOpacity: 1.0,
						strokeColor: '#808080',
						fillColor: '#808080',
					},
					disabled: {
						fillOpacity: 0.25,
						strokeOpacity: 1.0,
						strokeColor: '#808080',
						fillColor: '#808080',
					},
					'false-readonly': {
						fillOpacity: 0.0,
						strokeOpacity: 0.15,
						strokeColor: '#808080',
					},
					'true-readonly': {
						fillOpacity: 0.25,
						strokeOpacity: 1.0,
						strokeColor: '#808080',
						fillColor: '#808080',
					},
				} as Record<string, {
					strokeWeight?:number,
					fillOpacity?:number,
					strokeOpacity?:number,
					strokeColor?:string,
					fillColor?:string,
				}>,
			},

			/**
			 * Lista identyfikatorów lokalizacji odpowiadającym
			 * krają Unii Europejskiej
			 */
			locationEU: [
				'locationA',
				'locationB',
				'locationBg',
				'locationHr',
				'locationCy',
				'locationCz',
				'locationDk',
				'locationEst',
				'locationFin',
				'locationF',
				'locationGr',
				'locationE',
				'locationNl',
				'locationIrl',
				'locationLt',
				'locationLv',
				'locationL',
				'locationM',
				'locationDe',
				'locationP',
				'locationRo',
				'locationS',
				'locationSk',
				'locationSlo',
				'locationH',
				'locationI',
			],
		};
	},
	computed: {
		...mapState(useServicesStore, [
			'displaySaveChanges',
		]),
		...mapWritableState(useServicesStore, [
			'isFilterDirty',
		]),

		/**
		 * Czy zaznaczone są wszystkie kraje UE?
		 */
		isEUSelected() {
			const foreignItems = this.filter.locations.locationForeign.items;

			for (const key of this.locationEU) {
				if (!foreignItems[key].selected) {
					return false;
				}
			}
			return true;
		},
	},
	methods: {

		/**
		 * Inicjalizuje mapę w momencie jej wyświetlenia
		 */
		onMapVisible() {
			this.mapInit();
		},

		/**
		 * Pobiera Google Maps API, tworzy mapę oraz znajdujące się na niej klikalne rejony
		 */
		mapInit() {
			const { google }: any = window;

			if (typeof google?.maps === 'undefined') {
				// Załaduj skrypt z Google Maps, jeżeli nie istineje i ponownie wywołaj tą funkcję.
				const script = document.createElement('script');

				window.googleMapsInit = () => {
					this.mapInit();
				};

				script.src = '//maps.googleapis.com/maps/api/js?key=AIzaSyDS_5FIo6F1j4kR2ntTHtZWWFvljo_zPP4&callback=googleMapsInit';
				document.body.appendChild(script);

				return;
			}

			// Utwórz mapę.

			if (!this.$refs.GoogleMapElement) {
				return;
			}

			const map = new google.maps.Map(this.$refs.GoogleMapElement, {
				zoom: 4,
				maxZoom: 9,
				minZoom: 4,
				center: { lat: 51.935, lng: 16.892 },
				disableDefaultUI: true,
				gestureHandling: 'greedy',
			});

			// Ustaw wygląd rejonu w zależności od jego wyglądu.

			map.data.setStyle((f:any) => {
				const isReadonly = false;
				let style = String(f.getProperty('selected'));

				if (isReadonly) {
					style += '-readonly';
				}

				if (f.getProperty('disabled')) {
					style = 'disabled';
				}

				if (f.getProperty('blocked')) {
					style = 'blocked';
				}

				return {
					...this.map.styles.default,
					...this.map.styles[style],
				};
			});

			// Dodaj akcję kliknięcia w rejon.

			map.data.addListener('click', ({ feature } : { feature:any }) => {
				const isReadonly = false;

				if (isReadonly || feature.getProperty('blocked')) {
					return;
				}

				const isSelected = feature.getProperty('selected');
				feature.setProperty(
					'selected',
					!isSelected,
				);

				this.toggleLocation(feature.getProperty('name'), true);
			});

			// Dodaj klikalne rejony.

			const features = [];
			const selected = this.filter.location?.selected || [];
			const disabled:string[] = [];
			const blocked:string[] = [];

			for (const locationIndex in this.servicesObject.locations) {
				if (locationIndex === 'locationForeign') {
					continue;
				}
				const { items } = this.servicesObject.locations[locationIndex];

				// eslint-disable-next-line guard-for-in
				for (const itemIndex in items) {
					features.push({
						type: 'Feature',
						geometry: items[itemIndex].geo,
						id: itemIndex,
						properties: {
							name: itemIndex,
							region: locationIndex,
							selected:
								selected.indexOf(itemIndex) > -1 || selected.indexOf(locationIndex) > -1,
							disabled:
								disabled.indexOf(itemIndex) > -1 || disabled.indexOf(locationIndex) > -1,
							blocked:
								blocked.indexOf(itemIndex) > -1 || blocked.indexOf(locationIndex) > -1,
						},
					});
				}
			}

			map.data.addGeoJson({ type: 'FeatureCollection', features });
			this.map.google = map;

			window.setTimeout(() => {
				this.mapFitBounds();
			}, 600);
		},

		/**
		 * Zaznacza lub odznacza wszystkie kraje Unii Europejskiej
		 */
		toggleEU() {
			const foreignItems = this.filter.locations.locationForeign.items;
			this.locationEU
				.filter((key) => (!this.isEUSelected && !foreignItems[key].selected) || this.isEUSelected)
				.forEach((key) => this.toggleLocation(key));
		},

		/**
		 * Zaznacza lub odznacza rejon na mapie, a następnie ustawia
		 * tablicę zaznaczonych lokalizacji filtru.
		 */
		toggleLocation(locationName = '', isFromMap = false) {
			this.isFilterDirty = true;

			const change = { items: [] as string[], select: false, disableReason: '' };
			// Make the object...
			const { locations } = this.filter;
			const locationProperties = locations[locationName];

			if (locationProperties) {
				const areAllSelected = locationProperties.selected && locationProperties.selected === Object.keys(locationProperties.items).length;

				locationProperties.selected = areAllSelected ? 0 : Object.keys(locationProperties.items).length;
				change.select = !areAllSelected;

				// eslint-disable-next-line guard-for-in
				for (const itemIndex in locationProperties.items) {
					locationProperties.items[itemIndex].selected = !areAllSelected;
					change.items.push(itemIndex);
				}
			} else {
				// eslint-disable-next-line guard-for-in
				for (const index in locations) {
					const foundLocation = locations[index] && locations[index].items[locationName];

					if (foundLocation) {
						const isSelected = locations[index].items[locationName].selected;
						locations[index].items[locationName].selected = !isSelected;
						locations[index].selected = isSelected
							? locations[index].selected - 1
							: locations[index].selected + 1;
						change.select = !isSelected;

						change.items.push(locationName);
					}
				}
			}

			if (!isFromMap && this.map.google) {
				for (const index of change.items) {
					const feature = this.map.google.data.getFeatureById(index);

					if (!feature) {
						continue;
					}

					feature.setProperty('selected', change.select);
				}

				this.mapFitBounds();
			}

			// ...and cleanup this mess.
			const group = true;
			const result = [];

			// eslint-disable-next-line guard-for-in
			for (const index in locations) {
				const areAllSelected = locations[index].selected
					&& Object.keys(locations[index].items).length === locations[index].selected
					&& group;

				const isSelected = locations[index].selected;

				if (areAllSelected && group && index !== 'locationForeign') {
					result.push(index);

					continue;
				}

				if (!isSelected) {
					continue;
				}

				for (const itemIndex in locations[index].items) {
					if (locations[index].items[itemIndex].selected) {
						result.push(itemIndex);
					}
				}
			}

			if (this.filter.location) {
				this.filter.location.selected = result;
			}

			this.$emit('recalculate-potential-matches');
		},

		/**
		 * Próbuje dopasować mapę tak, aby zmieściły się na niej
		 * zaznaczone lokalizacje
		 */
		mapFitBounds() {
			const { google }: any = window;
			const map = this.map.google;
			const bounds = new google.maps.LatLngBounds();
			let locationCount = 0;

			if (!map) { return; }

			map.data.forEach((mapFragment:any) => {
				if (mapFragment.getProperty('selected')) {
					mapFragment.getGeometry()?.forEachLatLng((latlng:any) => {
						bounds.extend(latlng);
					});
					locationCount++;
				}
			});

			if (locationCount) {
				map.fitBounds(bounds, 16);
			}
		},
	},
};
</script>
